import { FC, ReactNode } from 'react';
import ContentLoader from 'react-content-loader';
import classNames from 'classnames';

// The loader needs real colour values, so it can't use our CSS colour tokens.
// We draw one loader for light mode and one for dark mode, and hide the one
// that doesn't match the current theme.
//
// Bands must contrast with what the skeleton sits on. On a panel the dark
// bands use the card colour; on a card (one step lighter) they step up again
// to the overlay colour, otherwise they'd match the card and disappear.
type Surface = 'panel' | 'card';

const THEMES: Record<
  Surface,
  { name: string; className: string; background: string; foreground: string }[]
> = {
  panel: [
    {
      name: 'light',
      className: 'dark:hidden',
      background: '#f5f6f7', // react-content-loader's default
      foreground: '#CED8F3',
    },
    {
      name: 'dark',
      className: 'hidden dark:block',
      background: '#22262F', // same as the surface-card token
      foreground: '#292E38', // same as the surface-overlay token
    },
  ],
  card: [
    {
      name: 'light',
      className: 'dark:hidden',
      background: '#eef0f3',
      foreground: '#CED8F3',
    },
    {
      name: 'dark',
      className: 'hidden dark:block',
      background: '#292E38', // same as the surface-overlay token
      foreground: '#333A4C', // same as the surface-selection-border token
    },
  ],
};

export interface SkeletonProps {
  /** Height in pixels. The width fills the container. */
  height: number;
  /** The placeholder shapes, usually SVG `<rect>`s. */
  children: ReactNode;
  className?: string;
  /** What the skeleton sits on, so the bands stay visible. */
  surface?: Surface;
}

/** A shimmering loading placeholder that works in light and dark mode. */
const Skeleton: FC<SkeletonProps> = ({
  height,
  children,
  className,
  surface = 'panel',
}) => (
  <div className={classNames('overflow-hidden', className)}>
    {THEMES[surface].map((theme) => (
      <ContentLoader
        key={theme.name}
        className={theme.className}
        backgroundColor={theme.background}
        foregroundColor={theme.foreground}
        width="100%"
        height={height}
      >
        {children}
      </ContentLoader>
    ))}
  </div>
);

export default Skeleton;
